import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PHASE_TEMPLATE } from "@/lib/basProcess/phases";

export interface CurrentPhase {
  key: string | null;
  name: string;
}

const EMPTY: CurrentPhase = { key: null, name: "Ej angivet" };

/** Lätt cache så att revisionsloggen slipper ett anrop per tangenttryck. */
const cache = new Map<string, CurrentPhase>();

const toPhase = (key: string | null | undefined): CurrentPhase =>
  key ? { key, name: PHASE_TEMPLATE.find((p) => p.key === key)?.name ?? key } : EMPTY;

/** Senast inlästa skedet oavsett projekt – för synkron kod utan projekt-id. */
let activePhase: CurrentPhase = EMPTY;

export const getActivePhase = (): CurrentPhase => activePhase;

/** Skedet som senast lästes in – används av synkron kod (t.ex. revisionsloggen). */
export const getCachedPhase = (projectId?: string | null): CurrentPhase => {
  if (!projectId) return EMPTY;
  return cache.get(projectId) ?? EMPTY;
};

export const fetchCurrentPhase = async (projectId: string): Promise<CurrentPhase> => {
  const { data } = await supabase
    .from("projects")
    .select("current_phase_key")
    .eq("id", projectId)
    .maybeSingle();
  const phase = toPhase(data?.current_phase_key ?? null);
  cache.set(projectId, phase);
  activePhase = phase;
  return phase;
};

/** Hämtar projektets aktiva skede och håller cachen varm. */
export const useCurrentPhase = (projectId?: string | null): CurrentPhase => {
  const [phase, setPhase] = useState<CurrentPhase>(() => getCachedPhase(projectId));

  useEffect(() => {
    if (!projectId) {
      setPhase(EMPTY);
      return;
    }
    let cancelled = false;
    fetchCurrentPhase(projectId).then((p) => {
      if (!cancelled) setPhase(p);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return phase;
};

export default useCurrentPhase;
